import { useMemo, useState } from "react";
import { Flex, LoadingIndicator, Popover, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { useGetPositionsForCaseQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { formatCountText } from "~/utils/strings";
import { POSITION_CASE_TYPES } from "../../../Documents/DocumentManagement/DocumentDetailsRightSidebar/DocumentStatusCard/FatcaAssignmentModal/constants";
import { getInvestingProfileName } from "../../../Profiles/utils";
import { type CaseProgram } from "../index";
import {
  item,
  popoverContent,
  section,
  sectionDivider,
  sectionHeader,
  sectionHeaderBanded,
  sectionLabel,
  trigger,
  triggerWrapper,
} from "./styles_vanilla.css";
import { getOtherProgramNames } from "./utils";

export type Props = {
  casePrograms: CaseProgram[];
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
};

export const ComplianceProgramDisplayV2 = ({ casePrograms, complianceCase }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const profileId = complianceCase?.investingProfile?.id ?? null;
  const investorProfileName = getInvestingProfileName(complianceCase?.investingProfile);

  const label = useMemo(
    () =>
      casePrograms.length === 1
        ? casePrograms[0].name
        : `Multiple programs (${casePrograms.length})`,
    [casePrograms],
  );

  const { data, loading } = useGetPositionsForCaseQuery({
    variables: { profileId: profileId ?? 0, caseTypes: [...POSITION_CASE_TYPES] },
    skip: !isOpen || !profileId,
    fetchPolicy: "cache-and-network",
  });

  const otherProgramNames = useMemo(
    () => getOtherProgramNames(data, casePrograms),
    [data, casePrograms],
  );

  const programCountLabel = formatCountText(casePrograms.length, "program");
  const isLoading = !!profileId && loading;
  const showOtherSection = !!profileId && !loading && otherProgramNames.length > 0;

  return (
    <Flex inline alignItems="center" className={triggerWrapper}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger>
          <button
            type="button"
            className={trigger}
            aria-haspopup="dialog"
            data-testid="compliance-program-value-v2"
          >
            <Text asSpan>{label}</Text>
            <Icon type="search" variant="outline" size="small" />
          </button>
        </Popover.Trigger>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          data-testid="compliance-program-popover-v2"
        >
          <Flex flexDirection="column" className={popoverContent}>
            {isLoading ? (
              <Flex
                alignItems="center"
                justifyContent="center"
                className={section}
                data-testid="compliance-program-popover-v2-loading"
              >
                <LoadingIndicator size="small" />
              </Flex>
            ) : (
              <>
                <Flex flexDirection="column" gap={Sizes.XS} className={section}>
                  <Text variant="upper-sm" className={sectionLabel}>
                    Investor profile
                  </Text>
                  <Text data-testid="compliance-program-popover-v2-investor">
                    {investorProfileName}
                  </Text>
                </Flex>
                <div className={sectionDivider} />
                <div data-testid="compliance-program-popover-v2-on-this-case">
                  <div className={sectionHeader}>
                    <Text variant="upper-sm" className={sectionLabel}>
                      On this case · {programCountLabel}
                    </Text>
                  </div>
                  <Flex flexDirection="column">
                    {casePrograms.map((program) => (
                      <Text key={program.id} className={item}>
                        {program.name}
                      </Text>
                    ))}
                  </Flex>
                </div>
                {showOtherSection && (
                  <div data-testid="compliance-program-popover-v2-other-positions">
                    <div className={sectionHeaderBanded}>
                      <Text variant="upper-sm" className={sectionLabel}>
                        Other positions under these programs
                      </Text>
                    </div>
                    <Flex flexDirection="column">
                      {otherProgramNames.map((name) => (
                        <Text key={name} className={item}>
                          {name}
                        </Text>
                      ))}
                    </Flex>
                  </div>
                )}
              </>
            )}
          </Flex>
        </Popover.Content>
      </Popover>
    </Flex>
  );
};
